import { callClaude } from '../lib/claude';
import { itemFeesTotal } from '../utils';
import React, { useState, useEffect } from 'react';

const PROPOSAL_STATUSES = ['Draft', 'Sent to Austin', 'Proposal Received', 'Proposal Verified', 'Sent to Client', 'Client Approved', 'Deposit Received', 'Paid in Full'];

const STATUS_COLORS = {
  'Draft': '#f5f5f5', 'Sent to Austin': '#e3f2fd', 'Proposal Received': '#fce4ec',
  'Proposal Verified': '#f9fbe7', 'Sent to Client': '#e8eaf6', 'Client Approved': '#f3e5f5',
  'Deposit Received': '#e0f2f1', 'Paid in Full': '#e8f5e9'
};
const STATUS_TEXT = {
  'Draft': '#555', 'Sent to Austin': '#0277bd', 'Proposal Received': '#c2185b',
  'Proposal Verified': '#558b2f', 'Sent to Client': '#283593', 'Client Approved': '#6a1b9a',
  'Deposit Received': '#00695c', 'Paid in Full': '#2e7d32'
};

const MARKUP = 1.30; // default - overridden per item below

function parseAmt(v) {
  const n = parseFloat((v || '').toString().replace(/[$,]/g, ''));
  return isNaN(n) ? 0 : n;
}

// Fee totals come from utils.itemFeesTotal (2026-10-06) — per-item fee
// markup rate; this file used to carry a 1.10 constant that also marked up
// sales tax and tariff and skipped installation.

const inp = {
  width: '100%', padding: '7px 10px', border: '1px solid #ddd',
  borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none',
  boxSizing: 'border-box', background: '#fff'
};

function fmt(n) {
  if (!n && n !== 0) return '—';
  return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function ProposalsTab({ items, projects, proposals, setProposals, onUpdateItem }) {
  // Legacy UI — the canonical Proposals tab is ProposalsTab2 (tab===15,
  // slug `/proposals`). This component is mounted at tab===2 (slug
  // `/proposals-pos`) but is no longer in NAV_GROUPS. The warn is here so
  // we can see in the console if anyone reaches it via a stale URL or
  // bookmark; if no hits land for two weeks the file + route can be deleted.
  useEffect(() => { console.warn('[legacy] ProposalsTab mounted'); }, []);

  const [view, setView] = useState('list'); // 'list' | 'new' | 'detail'
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [filterProject, setFilterProject] = useState('');
  const [bundleItems, setBundleItems] = useState([]);
  const [scanText, setScanText] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [approvalName, setApprovalName] = useState('');
  const [approvalDate, setApprovalDate] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');

  const parents = (items || []).filter(it => !it.parentId);

  // Items eligible for proposal (Presented to Client). Legacy tab; the
  // active Proposals UI is ProposalsTab2.
  const eligible = parents.filter(it =>
    it.status === 'Presented to Client' && !it.proposalId &&
    (!form?.project_label || it.projectLabel === form.project_label || it.project === form.project_label)
  );

  // Items already on a proposal
  function proposalItems(proposal) {
    return parents.filter(it =>
      (it.proposalId === proposal.id || (proposal.item_ids || []).includes(it.id)) &&
      (!proposal.project_label || it.projectLabel === proposal.project_label || it.project === proposal.project_label)
    );
  }

  function calcTotals(pItems) {
    const cost = pItems.reduce((s, it) => s + parseFloat(it.netPrice || it.netPrice || 0), 0);
    const fees = pItems.reduce((s, it) => s + itemFeesTotal(it), 0);
    return { cost, clientPrice: cost * (1 + (parseFloat(item.markupPct || 30) / 100)) + fees, fees };
  }

  // Create new proposal
  function startNew() {
    setForm({
      proposal_number: '',
      project_label: filterProject || '',
      notes: '',
      deposit_percent: 50,
      status: 'Draft',
    });
    setBundleItems([]);
    setView('new');
  }

  function toggleBundleItem(id) {
    setBundleItems(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  }

  function saveProposal() {
    if (!form.proposal_number.trim() || bundleItems.length === 0) {
      alert('Proposal number and at least one item are required.');
      return;
    }
    const pItems = parents.filter(it => bundleItems.includes(it.id));
    const { cost, clientPrice } = calcTotals(pItems);
    const newProposal = {
      id: Date.now().toString(),
      ...form,
      item_ids: bundleItems,
      total_cost: cost,
      total_client_price: clientPrice,
      created_at: new Date().toISOString(),
    };
    setProposals(prev => [...prev, newProposal]);
    // Update items with proposalId and advance to Sent to Austin
    bundleItems.forEach(id => {
      const item = parents.find(it => it.id === id);
      // Safety check - only update items that belong to this proposal's project
      if (item && (!form.project_label || item.projectLabel === form.project_label || item.project === form.project_label)) {
        onUpdateItem(id, { ...item, proposalId: newProposal.id, status: 'Sent to Austin' });
      }
    });
    setView('list');
    setForm(null);
    setBundleItems([]);
  }

  function updateProposal(id, patch) {
    setProposals(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  }

  function runScan(proposal) {
    if (!scanText.trim()) return;
    setScanLoading(true);
    setScanResult(null);
    const pItems = proposalItems(proposal);
    const itemList = pItems.map(it =>
      `- ${it.productName || it.item} | Cost: ${it.netPrice || it.netPrice || 'TBD'} | Client Price: ${fmt((parseFloat(it.netPrice || it.netPrice || 0)) * MARKUP)}`
    ).join('\n');
    const sys = 'You are a verification assistant for Cimon Starr Interior Design. Compare a proposal document against the expected line items. Return JSON only: {"match":true/false,"flags":[{"item":"","issue":"","docValue":"","expectedValue":""}],"summary":""}';
    const usr = `Expected items:\n${itemList}\n\nTotal expected cost: ${fmt(proposal.total_cost)}\nTotal expected client price: ${fmt(proposal.total_client_price)}\n\nProposal document:\n${scanText}`;

    fetch('/api/claude', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ system: sys, user: usr, max_tokens: 1000 })
    }).then(r => r.json()).then(d => {
      const text = d.text || '';
      try {
        setScanResult(JSON.parse(text.replace(/```json|```/g, '').trim()));
      } catch {
        setScanResult({ match: false, flags: [], summary: 'Could not parse — review manually.' });
      }
      setScanLoading(false);
    });
  }

  function markVerified(proposal) {
    updateProposal(proposal.id, { status: 'Proposal Verified' });
    proposalItems(proposal).forEach(it =>
      onUpdateItem(it.id, { ...it, status: 'Proposal Verified' })
    );
    setScanText(''); setScanResult(null);
  }

  function markClientApproved(proposal) {
    // Neutered 2026-05-09 — this legacy path advanced items to Client Approved
    // without assigning SKUs, walking assembly+parent peers, setting the
    // proposal's `approved_at`, or queueing the QB Product/Service push. Every
    // later QB validation rejects items advanced this way. The canonical path
    // is `approveProposal` in ProposalsTab2 (tab===15, slug `/proposals`).
    // See LIFECYCLES.md Appendix B #1.
    console.warn('[legacy] markClientApproved called; redirecting user to ProposalsTab2');
    alert('This action has moved. Use the Proposals tab.');
  }

  function markPaymentReceived(proposal) {
    if (!paymentAmount || !paymentDate) { alert('Please enter payment amount and date.'); return; }
    updateProposal(proposal.id, {
      status: 'Deposit Received',
      payment_amount: parseFloat(paymentAmount),
      payment_date: paymentDate,
    });
    proposalItems(proposal).forEach(it =>
      onUpdateItem(it.id, { ...it, status: 'Payment Received', paymentReceivedDate: paymentDate })
    );
    setPaymentAmount(''); setPaymentDate('');
  }

  const filtered = (proposals || []).filter(p =>
    !filterProject || p.project_label === filterProject
  );

  // ── NEW PROPOSAL FORM ──────────────────────────────────────────────────
  if (view === 'new') {
    const eligibleForProject = eligible.filter(it =>
      !form.project_label || it.projectLabel === form.project_label || it.project === form.project_label
    );
    const bundled = parents.filter(it => bundleItems.includes(it.id));
    const { cost, clientPrice } = calcTotals(bundled);

    return (
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => setView('list')} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>New Proposal Request</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PROPOSAL NUMBER</label>
            <input style={inp} placeholder="e.g. #0014" value={form.proposal_number}
              onChange={e => setForm(f => ({ ...f, proposal_number: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>PROJECT</label>
            <select style={inp} value={form.project_label} onChange={e => setForm(f => ({ ...f, project_label: e.target.value }))}>
              <option value="">— All Projects —</option>
              {(projects || []).map(p => <option key={p.id} value={p.label}>{p.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>DEPOSIT %</label>
            <input style={inp} type="number" value={form.deposit_percent}
              onChange={e => setForm(f => ({ ...f, deposit_percent: e.target.value }))} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>NOTES</label>
            <input style={inp} placeholder="Optional notes to Austin..." value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
            SELECT ITEMS TO INCLUDE ({bundleItems.length} selected)
          </div>
          {eligibleForProject.length === 0 ? (
            <div style={{ padding: 16, background: '#fafafa', borderRadius: 8, border: '1px dashed #ddd', color: '#aaa', fontSize: 13 }}>
              No Presented to Client items available{form.project_label ? ` for ${form.project_label}` : ''}.
            </div>
          ) : (
            <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f5f5f5' }}>
                    {['', 'Item', 'Room', 'Vendor', 'Net Cost', 'Client Price (30%)'].map(h => (
                      <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eligibleForProject.map((it, idx) => {
                    const cost = parseFloat(it.netPrice || it.netPrice || 0);
                    const checked = bundleItems.includes(it.id);
                    return (
                      <tr key={it.id} style={{ borderBottom: '1px solid #f5f5f5', background: checked ? '#f0f8ff' : idx % 2 === 0 ? '#fff' : '#fafafa', cursor: 'pointer' }}
                        onClick={() => toggleBundleItem(it.id)}>
                        <td style={{ padding: '8px 10px' }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleBundleItem(it.id)} />
                        </td>
                        <td style={{ padding: '8px 10px', fontWeight: 500 }}>{it.productName || it.item}</td>
                        <td style={{ padding: '8px 10px', color: '#666' }}>{it.roomName || '—'}</td>
                        <td style={{ padding: '8px 10px', color: '#666' }}>{it.vendor || '—'}</td>
                        <td style={{ padding: '8px 10px', color: '#666' }}>{fmt(cost)}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 600, color: '#2e7d32' }}>{fmt(cost * (1 + (parseFloat(item.markupPct || 30) / 100)))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {bundleItems.length > 0 && (
          <div style={{ background: '#f0f8ff', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 32 }}>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>ITEMS</div><div style={{ fontSize: 18, fontWeight: 700 }}>{bundleItems.length}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>NET COST</div><div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(cost)}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>CLIENT PRICE</div><div style={{ fontSize: 18, fontWeight: 700, color: '#2e7d32' }}>{fmt(clientPrice)}</div></div>
            <div><div style={{ fontSize: 10, fontWeight: 700, color: '#888' }}>MARGIN</div><div style={{ fontSize: 18, fontWeight: 700, color: '#4527a0' }}>{fmt(clientPrice - cost)}</div></div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={saveProposal} disabled={bundleItems.length === 0 || !form.proposal_number.trim()}
            style={{ padding: '9px 20px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: bundleItems.length > 0 && form.proposal_number ? 'pointer' : 'not-allowed', opacity: bundleItems.length > 0 && form.proposal_number ? 1 : 0.5 }}>
            Create Proposal Request
          </button>
          <button onClick={() => setView('list')} style={{ padding: '9px 16px', background: 'none', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, cursor: 'pointer', color: '#888' }}>
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── PROPOSAL DETAIL ────────────────────────────────────────────────────
  if (view === 'detail' && selected) {
    const proposal = (proposals || []).find(p => p.id === selected);
    if (!proposal) { setView('list'); return null; }
    const pItems = proposalItems(proposal);
    const needsScan = proposal.status === 'Proposal Received';
    const needsApproval = proposal.status === 'Proposal Verified' || proposal.status === 'Sent to Client';
    const needsPayment = proposal.status === 'Client Approved';

    return (
      <div style={{ maxWidth: 900 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <button onClick={() => { setView('list'); setScanText(''); setScanResult(null); }} style={{ background: 'none', border: 'none', color: '#1a56db', cursor: 'pointer', fontSize: 13 }}>← Back</button>
          <div style={{ fontWeight: 700, fontSize: 16 }}>Proposal {proposal.proposal_number}</div>
          <span style={{ background: STATUS_COLORS[proposal.status] || '#f5f5f5', color: STATUS_TEXT[proposal.status] || '#333', padding: '3px 10px', borderRadius: 10, fontSize: 11, fontWeight: 700 }}>{proposal.status}</span>
        </div>

        {/* Summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Net Cost', value: fmt(proposal.total_cost) },
            { label: 'Client Price', value: fmt(proposal.total_client_price), color: '#2e7d32' },
            { label: 'Margin', value: fmt(proposal.total_client_price - proposal.total_cost), color: '#4527a0' },
            { label: 'Items', value: pItems.length },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: '12px 14px', textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: s.color || '#1a1a1a' }}>{s.value}</div>
              <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Items table */}
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
          <div style={{ background: '#f5f5f5', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#555', borderBottom: '1px solid #e0e0e0' }}>LINE ITEMS</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                {['Item', 'Room', 'Vendor', 'Qty', 'Net Cost', 'Client Price'].map(h => (
                  <th key={h} style={{ padding: '7px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pItems.map((it, idx) => {
                const cost = parseFloat(it.netPrice || it.netPrice || 0);
                return (
                  <tr key={it.id} style={{ borderBottom: '1px solid #f5f5f5', background: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '7px 10px', fontWeight: 500 }}>{it.productName || it.item}</td>
                    <td style={{ padding: '7px 10px', color: '#666' }}>{it.roomName || '—'}</td>
                    <td style={{ padding: '7px 10px', color: '#666' }}>{it.vendor || '—'}</td>
                    <td style={{ padding: '7px 10px', color: '#666' }}>{it.qty || '1'}</td>
                    <td style={{ padding: '7px 10px', color: '#666' }}>{fmt(cost)}</td>
                    <td style={{ padding: '7px 10px', fontWeight: 600, color: '#2e7d32' }}>{fmt(cost * (1 + (parseFloat(item.markupPct || 30) / 100)))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Scan gate */}
        {needsScan && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#e65100', marginBottom: 12 }}>⚠ Verify Proposal from Austin</div>
            <textarea value={scanText} onChange={e => { setScanText(e.target.value); setScanResult(null); }}
              placeholder="Paste Austin's proposal text here..." rows={6}
              style={{ ...inp, resize: 'vertical', marginBottom: 10 }} />
            <button onClick={() => runScan(proposal)} disabled={!scanText.trim() || scanLoading}
              style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', marginRight: 8 }}>
              {scanLoading ? 'Verifying...' : 'Verify Proposal'}
            </button>
            {scanResult && (
              <div style={{ marginTop: 12 }}>
                <div style={{ background: scanResult.match ? '#e8f5e9' : '#fff5f5', border: `1px solid ${scanResult.match ? '#a5d6a7' : '#fcc'}`, borderRadius: 6, padding: '10px 14px', marginBottom: 10, fontSize: 13, fontWeight: 600, color: scanResult.match ? '#2e7d32' : '#c0392b' }}>
                  {scanResult.match ? '✓ Proposal matches — clear to approve' : '⚠ Discrepancies found — review before approving'}
                </div>
                {(scanResult.flags || []).map((fl, i) => (
                  <div key={i} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 6, padding: '8px 12px', marginBottom: 6, fontSize: 12 }}>
                    <div style={{ fontWeight: 700, color: '#e65100' }}>{fl.item}</div>
                    <div>Expected: {fl.expectedValue} | Got: {fl.docValue}</div>
                    {fl.issue && <div style={{ color: '#c0392b' }}>{fl.issue}</div>}
                  </div>
                ))}
                <button onClick={() => markVerified(proposal)}
                  style={{ marginTop: 10, padding: '8px 18px', background: scanResult.match ? '#1a1a1a' : '#e65100', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {scanResult.match ? 'Mark Verified — Approve to Austin' : 'Approve Anyway'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Client approval */}
        {needsApproval && (
          <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#6a1b9a', marginBottom: 12 }}>Mark Client Approved</div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 2 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>APPROVED BY</label>
                <input style={inp} placeholder="Client name" value={approvalName} onChange={e => setApprovalName(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>DATE</label>
                <input style={inp} type="date" value={approvalDate} onChange={e => setApprovalDate(e.target.value)} />
              </div>
            </div>
            <button onClick={() => markClientApproved(proposal)} disabled={!approvalName || !approvalDate}
              style={{ padding: '8px 18px', background: '#6a1b9a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: approvalName && approvalDate ? 1 : 0.5 }}>
              Mark Client Approved
            </button>
          </div>
        )}

        {/* Payment */}
        {needsPayment && (
          <div style={{ background: '#e0f2f1', border: '1px solid #80cbc4', borderRadius: 8, padding: 20, marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#00695c', marginBottom: 4 }}>Record Payment Received</div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 12 }}>
              Expected {proposal.deposit_percent}% deposit: {fmt(proposal.total_client_price * (proposal.deposit_percent / 100))}
            </div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>AMOUNT RECEIVED</label>
                <input style={inp} placeholder="$0" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>DATE</label>
                <input style={inp} type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />
              </div>
            </div>
            <button onClick={() => markPaymentReceived(proposal)} disabled={!paymentAmount || !paymentDate}
              style={{ padding: '8px 18px', background: '#00695c', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: paymentAmount && paymentDate ? 1 : 0.5 }}>
              Record Payment
            </button>
          </div>
        )}

        {/* Approval / payment summary */}
        {proposal.client_approval_name && (
          <div style={{ fontSize: 12, color: '#6a1b9a', marginBottom: 6 }}>✓ Approved by {proposal.client_approval_name} on {proposal.client_approval_date}</div>
        )}
        {proposal.payment_amount > 0 && (
          <div style={{ fontSize: 12, color: '#00695c' }}>$ Payment received: {fmt(proposal.payment_amount)} on {proposal.payment_date}</div>
        )}
      </div>
    );
  }

  // ── PROPOSAL LIST ──────────────────────────────────────────────────────
  const readyCount = eligible.length;

  const FEE_DISCLAIMER = 'Additional fees including freight, crating, and applicable taxes may apply where not itemized above.';

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={startNew} style={{ padding: '8px 18px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + New Proposal Request
        </button>
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          style={{ padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, background: '#fff' }}>
          <option value="">All Projects</option>
          {(projects || []).map(p => <option key={p.id} value={p.label}>{p.label}</option>)}
        </select>
        {readyCount > 0 && (
          <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '6px 12px', fontSize: 12, color: '#2e7d32', fontWeight: 600 }}>
            {readyCount} item{readyCount > 1 ? 's' : ''} ready to bundle into a proposal
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#bbb', fontSize: 13 }}>
          No proposals yet. Click + New Proposal Request to create one.
        </div>
      ) : (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                {['Proposal #', 'Project', 'Items', 'Net Cost', 'Client Price', 'Status', 'Approved By', 'Payment', ''].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, color: '#555', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((proposal, idx) => {
                const pItems = proposalItems(proposal);
                const needsAction = proposal.status === 'Proposal Received';
                return (
                  <tr key={proposal.id} style={{ borderBottom: '1px solid #f0f0f0', background: needsAction ? '#fffdf0' : idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 700 }}>{proposal.proposal_number}</td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>{proposal.project_label || '—'}</td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>{pItems.length}</td>
                    <td style={{ padding: '8px 10px', color: '#666' }}>{fmt(proposal.total_cost)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600, color: '#2e7d32' }}>{fmt(proposal.total_client_price)}</td>
                    <td style={{ padding: '8px 10px' }}>
                      <span style={{ background: STATUS_COLORS[proposal.status] || '#f5f5f5', color: STATUS_TEXT[proposal.status] || '#333', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                        {proposal.status}
                      </span>
                    </td>
                    <td style={{ padding: '8px 10px', color: '#666', fontSize: 11 }}>{proposal.client_approval_name || '—'}</td>
                    <td style={{ padding: '8px 10px', color: '#2e7d32', fontSize: 11, fontWeight: 600 }}>
                      {proposal.payment_amount > 0 ? fmt(proposal.payment_amount) : '—'}
                    </td>
                    <td style={{ padding: '8px 10px' }}>
                      <button onClick={() => { setSelected(proposal.id); setView('detail'); }}
                        style={{ padding: '4px 12px', background: needsAction ? '#fff8e1' : '#fff', border: `1px solid ${needsAction ? '#ffe082' : '#ddd'}`, borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: needsAction ? '#e65100' : '#444' }}>
                        {needsAction ? '⚠ Verify' : 'Open'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
